import { useApi } from '@backstage/core-plugin-api';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';
import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';
import { readerPath } from './readerPath';
import type { DocEntry } from './types';

const TECHDOCS_REF_ANNOTATION = 'metadata.annotations.backstage.io/techdocs-ref';

const toEntry = (entity: Entity): DocEntry => ({
  entityRef: stringifyEntityRef(entity),
  path: readerPath(entity),
  title: entity.metadata.title ?? entity.metadata.name,
  description: entity.metadata.description,
  ownerRef: entity.relations?.find(r => r.type === 'ownedBy')?.targetRef,
});

const byTitle = (a: { title: string }, b: { title: string }) =>
  a.title.localeCompare(b.title);

/** Every documented entity in the catalog — every entity carrying a techdocs-ref annotation. */
export const useDocCatalog = () => {
  const catalogApi = useApi(catalogApiRef);

  return useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: { [TECHDOCS_REF_ANNOTATION]: CATALOG_FILTER_EXISTS },
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'metadata.description',
        'relations',
      ],
    });

    return { all: items.map(toEntry).sort(byTitle) };
  }, [catalogApi]);
};
